import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { mockPhotos, mockVideos } from '../../mocks/gallery';

export default function Gallery() {
  const [tab, setTab] = useState<'photos' | 'videos'>('photos');
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [videoModal, setVideoModal] = useState<string | null>(null);

  return (
    <div className="bg-brand-dark min-h-screen">
      <Navbar />
      <main className="pt-20">
        <section className="py-16 px-6 md:px-12 bg-brand-dark">
          <div className="max-w-7xl mx-auto">
            <p className="font-heading text-brand-orange tracking-[0.3em] uppercase text-xs mb-2">Visuals</p>
            <h1 className="font-display text-6xl md:text-7xl text-white tracking-wide mb-8">GALLERY</h1>
            {/* Tabs */}
            <div className="inline-flex bg-brand-card p-1 rounded-full mb-10">
              {(['photos', 'videos'] as const).map((t) => (
                <button key={t} onClick={() => setTab(t)} className={`whitespace-nowrap font-heading font-semibold text-xs tracking-widest uppercase px-6 py-2 rounded-full transition-colors duration-200 cursor-pointer ${tab === t ? 'bg-brand-yellow text-black' : 'text-white/50 hover:text-white'}`}>
                  {t === 'photos' ? 'Photos' : 'Videos'}
                </button>
              ))}
            </div>

            {tab === 'photos' && (
              <div className="columns-2 md:columns-3 gap-3 space-y-3">
                {mockPhotos.map((p) => (
                  <div key={p.id} className="group relative overflow-hidden cursor-pointer break-inside-avoid" onClick={() => setLightbox(p.imageUrl)}>
                    <img src={p.imageUrl} alt={p.title} className="w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center gap-2">
                      <i className="ri-zoom-in-line text-white text-2xl" />
                      <span className="font-heading text-white text-xs tracking-widest uppercase">{p.title}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === 'videos' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {mockVideos.map((v) => {
                  const cardContent = (
                    <>
                      <div className="relative w-full h-0 pb-[56.25%] overflow-hidden bg-black">
                        <img src={v.thumbnailUrl} alt={v.title} className="absolute inset-0 w-full h-full object-contain transition-transform duration-500 group-hover:scale-105" />
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-brand-yellow group-hover:bg-brand-orange transition-colors duration-200">
                            <i className="ri-play-fill text-black text-2xl" />
                          </div>
                        </div>
                        <div className="absolute bottom-2 right-2 bg-black/70 px-2 py-0.5">
                          <span className="font-heading text-white text-xs">{v.duration}</span>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading font-semibold text-white text-sm leading-snug">{v.title}</h3>
                      </div>
                    </>
                  );

                  if (v.externalUrl) {
                    return (
                      <a
                        key={v.id}
                        href={v.externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group bg-brand-card overflow-hidden cursor-pointer block"
                      >
                        {cardContent}
                      </a>
                    );
                  }

                  return (
                    <div
                      key={v.id}
                      className="group bg-brand-card overflow-hidden cursor-pointer"
                      onClick={() => setVideoModal(v.videoUrl)}
                    >
                      {cardContent}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      {/* Photo Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-white cursor-pointer" onClick={() => setLightbox(null)}>
            <i className="ri-close-line text-2xl" />
          </button>
          <img src={lightbox} alt="Preview" className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* Video Modal */}
      {videoModal && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6" onClick={() => setVideoModal(null)}>
          <button className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-white cursor-pointer" onClick={() => setVideoModal(null)}>
            <i className="ri-close-line text-2xl" />
          </button>
          <div className="w-full max-w-3xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <iframe src={`${videoModal}?autoplay=1`} className="w-full h-full" allowFullScreen title="Video" />
          </div>
        </div>
      )}
    </div>
  );
}
